import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AudioState, AudioType } from '../audio.model';
import { Subscription } from 'rxjs';
import { AudioId as ImportedAudioId, AUDIO_LIBRARY } from '../audio.definitions';
import { AudioService } from '../audio.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@app/shared/components/button/button.component';

@Component({
  selector: 'slotcom-test-panel-audio',
  templateUrl: './test-panel-audio.component.html',
  styleUrl: './test-panel-audio.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent],
})
export class TestPanelAudioComponent implements OnInit, OnDestroy {
  volume: number = 0.75;

  isAllMuted: boolean = false;
  isBGMMuted: boolean = false;
  isSFXMuted: boolean = false;

  audioStates: { [key: string]: AudioState } = {};

  private subscriptions = new Subscription();

  AudioId = ImportedAudioId;
  SOUND_LIBRARY_ENTRIES = Object.values(AUDIO_LIBRARY);

  public readonly audioService = inject(AudioService);

  ngOnInit(): void {
    this.subscriptions.add(this.audioService.globalVolume$.subscribe((vol) => (this.volume = vol)));
    this.subscriptions.add(this.audioService.globalMute$.subscribe((muted) => (this.isAllMuted = muted)));
    this.subscriptions.add(
      this.audioService.typeMuteChange$.subscribe((change) => {
        if (change.type === 'BGM') {
          this.isBGMMuted = change.muted || this.isAllMuted;
        }
        if (change.type === 'SFX') {
          this.isSFXMuted = change.muted || this.isAllMuted;
        }
      })
    );
    this.subscriptions.add(
      this.audioService.audioStateChange$.subscribe(({ audioId, state }) => {
        this.audioStates[audioId] = state;
      })
    );

    this.SOUND_LIBRARY_ENTRIES.forEach((audio) => {
      this.audioStates[audio.id] = this.audioService.getAudioState(audio.id) || AudioState.IDLE;
    });
  }

  onVolumeChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.audioService.setGlobalVolume(parseFloat(target.value));
  }

  toggleMute(): void {
    this.audioService.toggleGlobalMute();
  }

  toggleMuteForType(type: AudioType): void {
    this.audioService.toggleMuteForType(type);
  }

  playAudio(id: string): void {
    this.audioService.play(id).catch((err) => console.warn(`UI: Could not play ${id}`, err));
  }

  pauseAudio(id: string): void {
    this.audioService.pause(id);
  }

  stopAudio(id: string): void {
    this.audioService.stop(id);
  }

  toggleLoop(id: string): void {
    const currentLoopStatus = this.audioService.isAudioLooping(id);
    this.audioService.setLoop(id, !currentLoopStatus);
  }

  isLooping(id: string): boolean {
    return this.audioService.isAudioLooping(id);
  }

  getAudioStateClass(id: string): string {
    const state = this.audioStates[id] || AudioState.IDLE;
    return `audio-state-${state}`;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
